import { ChangeDetectionStrategy, Component, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService, CoursesService, PathsService } from '@draeger-academy/common';
import { ConfigurationService } from '@draeger-academy/common/lib/services/configuration/configuration.service';
import { TranslateService } from '@ngx-translate/core';
import { CartItem } from 'models/cartItem';
import { MyCourseItem } from 'models/myCourse';
import { BehaviorSubject, Observable } from 'rxjs';
import { GenericModalService } from '../generic-modal';

@Component({
    selector: 'da-my-course-item',
    templateUrl: './my-course-item.component.html',
    styleUrls: ['./my-course-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyCourseItemComponent implements OnInit {
    @Input() course: MyCourseItem;
    @Input() status: any;
    @Input() testResults: any;
    @Input() mycourses: MyCourseItem[];
    public dynamicParameter: string;
    public language: string;
    public courseDetail: string;
    public booking: string;
    public courseImage: string;
    public alt = '';
    public currency = '';
    public bookingDetails = '';
    public countryName = '';
    public translationPrefix = 'myCourseItem';
    private _modalId: string;
    public currentStatus: string;
    public testResult: string = '';
    public twoG: string = '2G';
    public threeG: string = '3G';
    public loggedIn: boolean;
    private _$subModulesAvailable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    @ViewChild('twoGModal', { static: true }) private _2GModal;
    @ViewChild('threeGModal', { static: true }) private _3GModal;
    @ViewChild('overlay', { static: true }) private _overlay;
    @ViewChild('modal', { static: true }) private _modal;
    private _$course: BehaviorSubject<MyCourseItem> = new BehaviorSubject<MyCourseItem>(null);

    private _$location: BehaviorSubject<MyCourseItem> = new BehaviorSubject<MyCourseItem>(null);

    constructor(
        private translate: TranslateService,
        private _genericModalService: GenericModalService,
        public config: ConfigurationService,
        private _pathsService: PathsService,
        private authenticationService: AuthenticationService,
        private _courseService: CoursesService
    ) {}

    public get course$(): Observable<MyCourseItem> {
        return this._$course.asObservable();
    }

    public get subModulesAvailable$(): Observable<boolean> {
        return this._$subModulesAvailable.asObservable();
    }

    public get location$(): Observable<MyCourseItem> {
        return this._$location.asObservable();
    }

    private _$showVaccinationRules: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showVaccinationRules$(): Observable<boolean> {
        return this._$showVaccinationRules.asObservable();
    }


    // @HostListener('document:visibilitychange', ['$event'])
    // visibilitychange() {
    //     this.checkHiddenDocument();
    // }

    // checkHiddenDocument() {
    //     if (document.visibilityState === 'visible') {
    //         console.log('you are in the tab');
    //         this.getUserCourses();
    //     }
    // }

    ngOnInit() {
        this.getUserCourses();
        if (this.authenticationService.isLoggedIn()) {
            this.loggedIn = true;
        } else {
            this.loggedIn = false;
        }
        this.courseDetail = this._pathsService.getAccountCourseDetailLink(this.course.courseId);
        this.bookingDetails = this._pathsService.getAccountBookingDetailsLink(this.course.id);

        if (this.course) this._$course.next(this.course);
        if (this.config.isPartnerPage()) {
            this.translationPrefix = 'account.courseItem';
            this.translate.stream('common').subscribe((translation) => {
                this.currency = translation.currency;
            });
        } else {
            this.config.$siteSettings.subscribe((data) => {
                if (data && data.length > 0) {
                    this.currency = data.filter((f) => f.key.toLowerCase() === 'currency')[0].value;
                }
            });
        }

        if (this.status && this.status.length > 0)
            this.status.forEach((element) => {
                if (element.id === this.course.status) {
                    this.currentStatus = element.name;
                }
            });

        if (this.course.testResult && this.course.testResult.length > 0) {
            if (this.testResults && this.testResults.length > 0)
                this.testResults.forEach((element) => {
                    if (element.id === this.course.testResult) {
                        this.testResult = element.name;
                    }
                });
        }

        this.language = this.config.getCurrentSite();
        if (this.config.hasFeature('VaccinationRules')) this._$showVaccinationRules.next(true);
    }

    public editBooking(): void {
        this._overlayHandler(false);
    }

    public openOverlay(event: CartItem | MyCourseItem): void {
        this.config.$siteSettings.subscribe((data) => {
            if (data && data.length > 0) {
                const _countriesData = data.filter((f) => f.key.toLowerCase() === 'countries')[0];
                _countriesData['value'].filter((obj) => {
                    if (obj.code === event.location.countryKey) this.countryName = obj.name;
                });
                this._$location.next(this.course);
                this._overlayHandler(true);
            }
        });
    }

    private _overlayHandler(modal): void {
        if (modal) {
            this._modalId = this._genericModalService.create(this._overlay);
            this._genericModalService.open(this._modalId, this._overlay, true);
        } else {
            this._modalId = this._genericModalService.create(this._modal);
            this._genericModalService.open(this._modalId, this._modal, true);
        }
    }

    public closeModal() {
        this._genericModalService.close(this._modalId);
    }

    private _overlay2GHandler(): void {
        this._modalId = this._genericModalService.create(this._2GModal);
        this._genericModalService.open(this._modalId, this._2GModal, true);
    }

    private _overlay3GHandler(): void {
        this._modalId = this._genericModalService.create(this._3GModal);
        this._genericModalService.open(this._modalId, this._3GModal, true);
    }

    public showVaccinationRules(label: string) {
        if (label === '2G') this._overlay2GHandler();
        if (label === '3G') this._overlay3GHandler();
    }

    private getUserCourses() {
        if (this.course.infoPackageAvailable && this.course.infoPackageSubModuleIds.length > 0) {
            const arr = [];
            for (const i of this.mycourses) {
                if (this.course.infoPackageSubModuleIds.indexOf(i.id) !== -1) {
                    arr.push(i);
                }
            }
            if (arr.length > 0) {
                this._$subModulesAvailable.next(true);
            } else {
                this._$subModulesAvailable.next(false);
            }
        }
    }
}
